import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Calendar } from "lucide-react";
import {
	StatusChangeReason,
	STATUS_CHANGE_REASONS,
	WorkforceStatus,
} from "@/types/workforce";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface WorkforceEntry {
	id: string;
	status: WorkforceStatus;
	candidate_id: string;
	start_date: string;
	is_bench?: boolean;
	job?: { title: string } | null;
	external_job_title?: string | null;
	external_company_name?: string | null;
}

type Step = 'date' | 'ask' | 'reason';

interface CandidateUnavailableWorkforceModalProps {
	isOpen: boolean;
	onClose: () => void;
	candidateId: string;
	candidateName: string;
	currentUnavailableDate?: string | null;
	onConfirm: (unavailableUntil: string | null) => Promise<void>;
	onWorkforceClosed?: () => void;
}

export const CandidateUnavailableWorkforceModal = ({
	isOpen,
	onClose,
	candidateId,
	candidateName,
	currentUnavailableDate,
	onConfirm,
	onWorkforceClosed,
}: CandidateUnavailableWorkforceModalProps) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<Step>('date');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [activeEntries, setActiveEntries] = useState<WorkforceEntry[]>([]);
	const [loading, setLoading] = useState(true);

	// Unavailable until date state
	const [selectedDate, setSelectedDate] = useState<string>("");

	// Reason form state
	const [targetStatus, setTargetStatus] = useState<'terminated' | 'completed'>('terminated');
	const [reason, setReason] = useState<StatusChangeReason | "">("");
	const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
	const [note, setNote] = useState("");

	// Fetch active workforce entries for this candidate
	useEffect(() => {
		if (!isOpen || !candidateId) return;

		const fetchEntries = async () => {
			setLoading(true);
			const { data, error } = await supabase
				.from('workforce')
				.select('id, status, candidate_id, start_date, is_bench, job:job_id(title), external_job_title, external_company_name')
				.eq('candidate_id', candidateId)
				.in('status', ['active', 'on_hold'])
				.eq('is_bench', false);

			if (!error && data)
				setActiveEntries(data as unknown as WorkforceEntry[]);
			else
				setActiveEntries([]);

			setLoading(false);
		};

		fetchEntries();
	}, [isOpen, candidateId]);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setStep('date');
			setTargetStatus('terminated');
			setReason("");
			setEndDate(new Date().toISOString().split("T")[0]);
			setNote("");

			// Initialize unavailable date
			if (currentUnavailableDate)
				setSelectedDate(currentUnavailableDate.split('T')[0]);
			else {
				const tomorrow = new Date();
				tomorrow.setDate(tomorrow.getDate() + 1);
				setSelectedDate(tomorrow.toISOString().split('T')[0]);
			}
		}
	}, [isOpen, currentUnavailableDate]);

	const handleDateConfirm = async () => {
		setIsSubmitting(true);
		try {
			await onConfirm(selectedDate || null);

			// If there are active workforce entries, go to the ask step
			if (!loading && activeEntries.length > 0)
				setStep('ask');
			else
				onClose();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDateSkip = async () => {
		setIsSubmitting(true);
		try {
			await onConfirm(null);

			if (!loading && activeEntries.length > 0)
				setStep('ask');
			else
				onClose();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCloseWorkforce = async () => {
		if (!reason) return;

		setIsSubmitting(true);

		try {
			const entryIds = activeEntries.map(e => e.id);

			const { error } = await supabase
				.from('workforce')
				.update({
					status: targetStatus,
					end_date: endDate,
					status_change_reason: reason,
					status_change_note: note.trim() || null,
				})
				.in('id', entryIds);

			if (error) throw error;

			toast({
				title: t("common.success", "Success"),
				description: t("candidates.unavailableWorkforce.closeSuccess", "{{count}} workforce assignment(s) closed", { count: activeEntries.length }),
			});

			onWorkforceClosed?.();
			onClose();
		} catch {
			toast({
				title: t("common.error", "Error"),
				description: t("candidates.unavailableWorkforce.closeError", "Failed to close workforce assignments"),
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-md">
				{step === 'date' && (
					<>
						<DialogHeader>
							<DialogTitle>
								{t("candidates.unavailableUntil.title", "Unavailable Until")}
							</DialogTitle>
							<DialogDescription>
								{t("candidates.unavailableUntil.description", "Set the date when this candidate will be available again")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="unavailable-date" className="flex items-center gap-2">
									<Calendar className="h-4 w-4" />
									{t("candidates.unavailableUntil.dateLabel", "Available From")}
								</Label>
								<Input
									id="unavailable-date"
									type="date"
									value={selectedDate}
									onChange={(e) => setSelectedDate(e.target.value)}
									min={new Date().toISOString().split('T')[0]}
								/>
								<p className="text-sm text-gray-500">
									{t("candidates.unavailableUntil.helper", "Optional: Leave empty if the date is unknown")}
								</p>
							</div>
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={handleDateSkip} disabled={isSubmitting}>
								{t("candidates.unavailableUntil.skip", "Skip")}
							</Button>
							<Button onClick={handleDateConfirm} disabled={isSubmitting}>
								{t("common.confirm", "Confirm")}
							</Button>
						</DialogFooter>
					</>
				)}

				{step === 'ask' && (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<AlertTriangle className="h-5 w-5 text-amber-500" />
								{t("candidates.unavailableWorkforce.title", "Close Workforce Assignments")}
							</DialogTitle>
							<DialogDescription>
								{t("candidates.unavailableWorkforce.description", "{{name}} has been marked as unavailable. There are {{count}} active workforce assignment(s). Would you like to close them?", { name: candidateName, count: activeEntries.length })}
							</DialogDescription>
						</DialogHeader>

						{/* List active entries */}
						<div className="space-y-2 py-2">
							{activeEntries.map((entry) => {
								const title = entry.job?.title || entry.external_job_title || entry.external_company_name || t("workforce.fields.untitled", "Untitled");
								return (
									<div key={entry.id} className="flex items-center gap-2 p-2 rounded-sm bg-muted/50 text-sm">
										<div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
										<span>{title}</span>
										<span className="text-muted-foreground ml-auto text-xs">
											{t(`workforce.statuses.${entry.status}`, entry.status)}
										</span>
									</div>
								);
							})}
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={onClose}>
								{t("candidates.unavailableWorkforce.skip", "No, keep them")}
							</Button>
							<Button variant="destructive" onClick={() => setStep('reason')}>
								{t("candidates.unavailableWorkforce.close", "Yes, close them")}
							</Button>
						</DialogFooter>
					</>
				)}

				{step === 'reason' && (
					<>
						<DialogHeader>
							<DialogTitle>
								{t("candidates.unavailableWorkforce.reasonTitle", "Reason for Closing")}
							</DialogTitle>
							<DialogDescription>
								{t("candidates.unavailableWorkforce.reasonDescription", "Please provide a reason for closing these assignments.")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-2">
							{/* Target status */}
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									{t("candidates.unavailableWorkforce.targetStatus", "Close as")}
								</Label>
								<Select value={targetStatus} onValueChange={(v) => setTargetStatus(v as 'terminated' | 'completed')}>
									<SelectTrigger className="h-11">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="terminated">
											{t("workforce.statuses.terminated", "Terminated")}
										</SelectItem>
										<SelectItem value="completed">
											{t("workforce.statuses.completed", "Completed")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							{/* Reason (mandatory) */}
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									{t("workforce.statusChange.reason", "Reason")}
									<span className="text-red-500 ml-1">*</span>
								</Label>
								<Select value={reason} onValueChange={(v) => setReason(v as StatusChangeReason)}>
									<SelectTrigger className="h-11">
										<SelectValue placeholder={t("workforce.statusChange.selectReason", "Select a reason...")} />
									</SelectTrigger>
									<SelectContent>
										{STATUS_CHANGE_REASONS.map((r) => (
											<SelectItem key={r.value} value={r.value}>
												{t(`workforce.statusChange.reasons.${r.value}`, r.label)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* End date */}
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									{t("workforce.fields.endDate", "End Date")}
									<span className="text-red-500 ml-1">*</span>
								</Label>
								<Input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									className="h-11"
								/>
							</div>

							{/* Optional note */}
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									{t("workforce.statusChange.note", "Note")}
									<span className="text-muted-foreground text-xs ml-1">({t("common.optional", "optional")})</span>
								</Label>
								<Textarea
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder={t("workforce.statusChange.notePlaceholder", "Add a note about this status change...")}
									rows={2}
									className="resize-none"
								/>
							</div>
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={() => setStep('ask')}>
								{t("common.back", "Back")}
							</Button>
							<Button
								variant="destructive"
								onClick={handleCloseWorkforce}
								disabled={isSubmitting || !reason || !endDate}
							>
								{isSubmitting
									? t("common.closing", "Closing...")
									: t("candidates.unavailableWorkforce.confirm", "Close Assignments")
								}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
